import React, { useState } from 'react';
import { X } from 'lucide-react';

/**
 * 키워드 칩 3줄 — 포함(하나라도) · 꼭 포함(전부) · 제외.
 *
 * 저장 형식은 그대로 둔다: `keyword` = `의료,헬스케어+컨설팅` (`,` OR · `+` AND),
 * `exclude` = `유지보수,청소`. 수집 기준 DB · 알림이 이 문자열을 읽는다.
 */
export interface KeywordValue {
  keyword: string;
  exclude: string;
}

const words = (s: string) => s.split(',').map((w) => w.trim()).filter(Boolean);

function parse(v: KeywordValue) {
  const [first = '', ...rest] = v.keyword.split('+');
  return {
    any: words(first),
    // `+` 뒤 묶음은 하나씩 칩. 예전에 손으로 쓴 `a+b,c` 는 「b,c」 칩 하나로 남는다
    all: rest.map((g) => g.split(',').map((w) => w.trim()).filter(Boolean).join(',')).filter(Boolean),
    not: words(v.exclude),
  };
}

function serialize(any: string[], all: string[], not: string[]): KeywordValue {
  const groups = [...(any.length ? [any.join(',')] : []), ...all];
  return { keyword: groups.join('+'), exclude: not.join(',') };
}

type Key = 'any' | 'all' | 'not';

const ROWS: { key: Key; label: string; placeholder: string; chip: string }[] = [
  { key: 'any', label: '포함', placeholder: '하나라도 있으면', chip: 'bg-indigo-50 text-indigo-700' },
  { key: 'all', label: '꼭 포함', placeholder: '전부 있어야', chip: 'bg-emerald-50 text-emerald-700' },
  { key: 'not', label: '제외', placeholder: '있으면 뺌', chip: 'bg-red-50 text-red-700' },
];

/** 읽기 전용 — 목록 한 줄에 작은 칩으로 */
export const KeywordChips: React.FC<{ value: KeywordValue }> = ({ value }) => {
  const p = parse(value);
  const chips = ROWS.flatMap((r) => p[r.key].map((w) => ({ w, r })));
  if (!chips.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {chips.map(({ w, r }) => (
        <span key={`${r.key}:${w}`} title={r.label} className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${r.chip}`}>
          {r.key === 'not' ? `−${w}` : w}
        </span>
      ))}
    </span>
  );
};

export const KeywordFields: React.FC<{
  value: KeywordValue;
  onChange: (v: KeywordValue) => void;
}> = ({ value, onChange }) => {
  const parsed = parse(value);
  const [draft, setDraft] = useState<Record<Key, string>>({ any: '', all: '', not: '' });

  const set = (key: Key, list: string[]) => {
    const next = { ...parsed, [key]: list };
    onChange(serialize(next.any, next.all, next.not));
  };

  const add = (key: Key, text: string) => {
    const cur = parsed[key];
    const fresh = words(text).filter((w) => !cur.includes(w));
    setDraft((d) => ({ ...d, [key]: '' }));
    if (fresh.length) set(key, [...cur, ...fresh]);
  };

  return (
    <div className="space-y-2">
      {ROWS.map((r) => (
        <div key={r.key} className="flex items-center gap-3">
          <span className="w-14 flex-shrink-0 text-xs font-medium text-slate-500">{r.label}</span>
          <div className="flex min-h-[38px] min-w-0 flex-1 flex-wrap items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 py-1 focus-within:ring-2 focus-within:ring-indigo-500">
            {parsed[r.key].map((w, i) => (
              <span key={w} className={`inline-flex items-center gap-0.5 rounded-full py-0.5 pl-2.5 pr-1 text-xs font-medium ${r.chip}`}>
                {w}
                <button
                  type="button"
                  aria-label={`${w} 빼기`}
                  onClick={() => set(r.key, parsed[r.key].filter((_, j) => j !== i))}
                  className="rounded-full p-0.5 hover:bg-black/5"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
            <input
              type="text"
              value={draft[r.key]}
              aria-label={r.label}
              placeholder={parsed[r.key].length ? '' : r.placeholder}
              onChange={(e) => {
                const v = e.target.value;
                if (v.includes(',')) add(r.key, v);
                else setDraft((d) => ({ ...d, [r.key]: v }));
              }}
              onKeyDown={(e) => {
                const v = draft[r.key];
                // 쓰던 말이 있으면 칩으로. 비어 있으면 Enter 는 그대로 검색이다
                if (e.key === 'Enter' && v.trim()) {
                  e.preventDefault();
                  add(r.key, v);
                }
                if (e.key === 'Backspace' && !v && parsed[r.key].length) {
                  set(r.key, parsed[r.key].slice(0, -1));
                }
              }}
              // Enter 없이 검색을 눌러도 쓰던 말이 빠지지 않게
              onBlur={() => draft[r.key].trim() && add(r.key, draft[r.key])}
              className="min-w-[100px] flex-1 border-0 bg-transparent px-1 py-1 text-sm focus:outline-none"
            />
          </div>
        </div>
      ))}
    </div>
  );
};
